import { ZodTypeDef } from 'zod';

import { RequestBuilder } from './request/RequestBuilder';
import { RequestBuilderOptions, RequestMethod } from './utils/types';

/* ----------------------------- Request Method ----------------------------- */

/**
 * request
 *
 * Wrapper function to conveniently return an instance of RequestBuilder
 *
 * @param method - HTTP request method
 * @param endpoint - The location to send the request
 * @param opts - Options to add to the request, can be added later by using chain methods
 */
export const request = <
    Output,
    Def extends ZodTypeDef = ZodTypeDef,
    Input = Output,
>(
    method: RequestMethod,
    endpoint: string,
    baseUrl: string,
    opts: RequestBuilderOptions<Output, Def, Input>,
) => new RequestBuilder(method, endpoint, baseUrl, opts);

/* --------------------------- Convenience Methods -------------------------- */

/**
 * get
 *
 * Wrapper to quickly return an instance of RequestBuilder using the GET method
 *
 * @param endpoint - The location to send the request
 * @param opts - Options to add to the request, can be added later by using chain methods
 */
export const get = <
    Output,
    Def extends ZodTypeDef = ZodTypeDef,
    Input = Output,
>(
    endpoint: string,
    baseUrl: string,
    opts: RequestBuilderOptions<Output, Def, Input>,
) => request(RequestMethod.GET, endpoint, baseUrl, opts);

/**
 * head
 *
 * Wrapper to quickly return an instance of RequestBuilder using the HEAD method
 *
 * @param endpoint - The location to send the request
 * @param opts - Options to add to the request, can be added later by using chain methods
 */
export const head = <
    Output,
    Def extends ZodTypeDef = ZodTypeDef,
    Input = Output,
>(
    endpoint: string,
    baseUrl: string,
    opts: RequestBuilderOptions<Output, Def, Input>,
) => request(RequestMethod.HEAD, endpoint, baseUrl, opts);

/**
 * post
 *
 * Wrapper to quickly return an instance of RequestBuilder using the POST method
 *
 * @param endpoint - The location to send the request
 * @param opts - Options to add to the request, can be added later by using chain methods
 */
export const post = <
    Output,
    Def extends ZodTypeDef = ZodTypeDef,
    Input = Output,
>(
    endpoint: string,
    baseUrl: string,
    opts: RequestBuilderOptions<Output, Def, Input>,
) => request(RequestMethod.POST, endpoint, baseUrl, opts);

/**
 * put
 *
 * Wrapper to quickly return an instance of RequestBuilder using the PUT method
 *
 * @param endpoint - The location to send the request
 * @param opts - Options to add to the request, can be added later by using chain methods
 */
export const put = <
    Output,
    Def extends ZodTypeDef = ZodTypeDef,
    Input = Output,
>(
    endpoint: string,
    baseUrl: string,
    opts: RequestBuilderOptions<Output, Def, Input>,
) => request(RequestMethod.PUT, endpoint, baseUrl, opts);

/**
 * del
 *
 * Wrapper to quickly return an instance of RequestBuilder using the DELETE method
 *
 * @param endpoint - The location to send the request
 * @param opts - Options to add to the request, can be added later by using chain methods
 */
export const del = <
    Output,
    Def extends ZodTypeDef = ZodTypeDef,
    Input = Output,
>(
    endpoint: string,
    baseUrl: string,
    opts: RequestBuilderOptions<Output, Def, Input>,
) => request(RequestMethod.DELETE, endpoint, baseUrl, opts);

/**
 * options
 *
 * Wrapper to quickly return an instance of RequestBuilder using the OPTIONS method
 *
 * @param endpoint - The location to send the request
 * @param opts - Options to add to the request, can be added later by using chain methods
 */
export const options = <
    Output,
    Def extends ZodTypeDef = ZodTypeDef,
    Input = Output,
>(
    endpoint: string,
    baseUrl: string,
    opts: RequestBuilderOptions<Output, Def, Input>,
) => request(RequestMethod.OPTIONS, endpoint, baseUrl, opts);

/**
 * patch
 *
 * Wrapper to quickly return an instance of RequestBuilder using the PATCH method
 *
 * @param endpoint - The location to send the request
 * @param opts - Options to add to the request, can be added later by using chain methods
 */
export const patch = <
    Output,
    Def extends ZodTypeDef = ZodTypeDef,
    Input = Output,
>(
    endpoint: string,
    baseUrl: string,
    opts: RequestBuilderOptions<Output, Def, Input>,
) => request(RequestMethod.PATCH, endpoint, baseUrl, opts);

export const remote = {
    request,
    get,
    head,
    post,
    put,
    del,
    options,
    patch,
};
